import { useLocalStorageState } from 'ahooks';
import { isDateFilterActive } from '../../functions/date.functions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tAdvanceSearchLead } from '../../components/AdvanceSearch/AdvanceSearchModal.types.ts';
import { eDeleteFunnelProgressModal } from '../../pages/Pipe/modals/DeleteFunnelProgressModal/DeleteFunnelProgressModal.enum.ts';
import {
  tFunnelStatus,
  tFunnelsWithStages,
  tTagFilter,
} from '../../pages/Pipe/Pipe.types.ts';
import { eChannelType } from '../SocketsProvider/Sockets.enums.ts';
import { tStatusFilter } from '../SocketsProvider/Sockets.types.ts';
import { useSubscriptionContext } from '../SubscriptionProvider/Subscription.context.ts';
import { SyncConversationsEnum } from '../SyncConversationsProvider/SyncConversations.enum.ts';
import { useIntegrationsContext } from '../IntegrationsProvider/Integrations.context.ts';
import { tWorkspaceUserInfo } from '../WorkspaceProvider/Workspace.types.ts';
import { VITE_TIKTOK_FEATURE } from 'src/constants/envs.ts';
import {
  eDateFilterType,
  tFocusedChatEvent,
  tModalsContext,
  tTagsMatch,
  tWhatsAppConnectionModalOptions,
  tWhatsAppConnectionSource,
} from './Modals.types.ts';

// ModalsProvider latches the legacy choice with the rest of the opening, so
// it adds that member to the context itself.
const useModals = (): Omit<
  tModalsContext,
  'whatsAppBusinessConnectionModalIsLegacyChosen'
> => {
  const {
    shouldSeePrevPastDueModal,
    shouldSeeNoPaidModal,
    shouldSeePastDueModal,
    shouldSeeConfirmationModal,
  } = useSubscriptionContext();
  const { hasTiktokIntegration } = useIntegrationsContext();

  const [addWorkspaceModelIsOpen, setAddWorkspaceModalIsOpen] =
    useState<boolean>(false);
  const [connectionModalIsOpen, setConnectionModalIsOpen] =
    useState<boolean>(false);
  const [disconnectionModalIsOpen, setDisconnectionModalIsOpen] =
    useState<boolean>(false);
  const [forceDisconnectionModalIsOpen, setForceDisconnectionModalIsOpen] =
    useState<boolean>(false);
  const [metaConnectionModalIsOpen, setMetaConnectionModalIsOpen] =
    useState<boolean>(false);
  const [
    metaConnectionModalReconnectRequested,
    setMetaConnectionModalReconnectRequested,
  ] = useState<boolean>(false);
  const [metaPagesModalIsOpen, setMetaPagesModalIsOpen] =
    useState<boolean>(false);
  const [
    whatsAppBusinessConnectionModalIsOpen,
    setWhatsAppBusinessConnectionModalOpenState,
  ] = useState<boolean>(false);
  const [
    whatsAppBusinessConnectionModalSource,
    setWhatsAppBusinessConnectionModalSource,
  ] = useState<tWhatsAppConnectionSource | null>(null);
  const [
    whatsAppBusinessConnectionModalExitDestination,
    setWhatsAppBusinessConnectionModalExitDestination,
  ] = useState<string | null>(null);
  // Every open replaces the source, so a reopen without one never inherits the
  // previous opener's; closing clears it. The exit destination mirrors it:
  // every close replaces it and every open clears it.
  const setWhatsAppBusinessConnectionModalIsOpen = useCallback(
    (value: boolean, options?: tWhatsAppConnectionModalOptions) => {
      setWhatsAppBusinessConnectionModalOpenState(value);
      setWhatsAppBusinessConnectionModalSource(
        value ? (options?.source ?? null) : null,
      );
      setWhatsAppBusinessConnectionModalExitDestination(
        value ? null : (options?.exitDestination ?? null),
      );
    },
    [],
  );
  const [syncConversationsStatus, setSyncConversationsStatus] =
    useState<SyncConversationsEnum>(SyncConversationsEnum.HIDDEN);
  const [showNoPaidModal, toggleNoPaidModal] = useState<boolean>(false);
  const [showPastDueModal, togglePastDueModal] = useState<boolean>(false);
  const [showTrialConfirmModal, toggleTrialConfirmModal] =
    useState<boolean>(false);
  const [showPrevPastDueModal, togglePrevPastDueModal] = useState(false);
  const [changeFunnelModalIsOpen, setChangeFunnelModalIsOpen] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [advanceSearchModalIsOpen, setAdvanceSearchModalIsOpen] =
    useState(false);
  const [selectedLeadForChatModal, setSelectedLeadForChatModal] =
    useState<tAdvanceSearchLead | null>(null);
  // SRE-3328. Set by an inbox message-search hit before it hands over the lead,
  // consumed once by MessageContainer when the matching thread is on screen.
  const [focusedChatEvent, setFocusedChatEvent] =
    useState<tFocusedChatEvent | null>(null);
  const [openAdvanceFiltersModal, setOpenAdvanceFiltersModal] = useState(false);
  const [chatModalIsOpen, setChatModalIsOpen] = useState(false);
  const [maximizeModal, setMaximizeModal] = useState(false);
  const [currentFunnelStatus, setCurrentFunnelStatus] = useState<
    tFunnelStatus[]
  >([]);
  const [showSubscribeIntegrationsModal, setShowSubscribeIntegrationsModal] =
    useState(false);
  const [showSubscribeAddUsersModal, setShowSubscribeAddUsersModal] =
    useState(false);
  const [showSubscribeImportLeadsModal, setShowSubscribeImportLeadsModal] =
    useState(false);
  const [showBankDetailsModal, toggleBankDetailsModal] = useState(false);
  const [
    addOptionsSecondLevelModalIsOpen,
    setAddOptionsSecondLevelModalIsOpen,
  ] = useState(false);
  const [addOptionsThirdLevelModalIsOpen, setAddOptionsThirdLevelModalIsOpen] =
    useState(false);
  const [changesDataModalIsOpen, setChangesDataModalIsOpen] = useState(false);
  const [showArchiveLeadsModal, setShowArchiveLeadsModal] = useState(false);
  const [tiktokConnectionModalIsOpen, setTiktokConnectionModalIsOpen] =
    useState<boolean>(false);
  const tagOpsInFlight = useRef(0);

  // General filters
  const [messageReadFilter, setMessageReadFilter] = useLocalStorageState<
    string | undefined
  >('messageReadFilter');
  const [integrationFilter, setIntegrationFilter] = useLocalStorageState<
    eChannelType | undefined
  >('integrationFilter');
  const [statusFilter, setStatusFilter] = useLocalStorageState<
    tStatusFilter | undefined
  >('statusFilter');
  const [tagsFilter, setTagsFilter] = useLocalStorageState<
    Array<tTagFilter> | undefined
  >('tagsFilter');
  const [assignedUserFilters, setAssignedUserFilters] = useLocalStorageState<
    Partial<tWorkspaceUserInfo> | undefined
  >('assignedUserFilters');
  const [dateRange, setDateRange] = useLocalStorageState<
    [Date | undefined, Date | undefined] | undefined
  >('dateRange');
  const [dateFilterType, setDateFilterType] = useLocalStorageState<
    eDateFilterType | undefined
  >('dateFilterType');
  // Directory only (RET-2331), not stored: see `directoryFunnelFilter` in
  // the context type.
  const [directoryFunnelFilter, setDirectoryFunnelFilter] = useState('all');
  const [directoryStageFilter, setDirectoryStageFilter] = useState('all');
  const [directoryFunnels, setDirectoryFunnels] = useState<
    tFunnelsWithStages[] | null
  >(null);
  // Stored like the tags it qualifies. Anything stored but 'all' reads as
  // 'any', what the filter always did, and 'any' is stored as nothing.
  const [storedTagsMatch, setStoredTagsMatch] = useLocalStorageState<
    string | undefined
  >('directoryTagsMatch');
  const directoryTagsMatch: tTagsMatch = storedTagsMatch === 'all' ? 'all' : 'any';
  const setDirectoryTagsMatch = useCallback(
    (value: tTagsMatch) => setStoredTagsMatch(value === 'all' ? 'all' : undefined),
    [setStoredTagsMatch],
  );
  // INT-1596: session-only on purpose, unlike the stored filters (see the type).
  const [undeliveredFilter, setUndeliveredFilter] = useState<boolean>(false);

  useEffect(() => {
    toggleNoPaidModal(shouldSeeNoPaidModal);
    togglePastDueModal(shouldSeePastDueModal);
    toggleTrialConfirmModal(shouldSeeConfirmationModal);
    togglePrevPastDueModal(shouldSeePrevPastDueModal);
  }, [
    shouldSeePrevPastDueModal,
    shouldSeeNoPaidModal,
    shouldSeePastDueModal,
    shouldSeeConfirmationModal,
  ]);

  // Clear stale TikTok filter when the gating it depended on flips off,
  // otherwise the radio hides but the localStorage value still drives a
  // `integration=TIKTOK` query and the directory silently returns zero leads.
  const isTiktokConnected = hasTiktokIntegration();
  useEffect(() => {
    if (
      integrationFilter === eChannelType.TIKTOK &&
      (!VITE_TIKTOK_FEATURE || !isTiktokConnected)
    ) {
      setIntegrationFilter(undefined);
    }
  }, [isTiktokConnected, integrationFilter, setIntegrationFilter]);
  const [changeServiceModalIsOpen, setChangeServiceModalIsOpen] =
    useState<boolean>(false);
  const [addOptionsModalIsOpen, setAddOptionsModalIsOpen] =
    useState<boolean>(false);
  const [deleteOptionModalIsOpen, setDeleteOptionModalIsOpen] =
    useState<boolean>(false);
  const [integrationWhatsAppModalIsOpen, setIntegrationWhatsAppModalIsOpen] =
    useState<boolean>(false);
  const [integrationInstagramModalIsOpen, setIntegrationInstagramModalIsOpen] =
    useState<boolean>(false);
  const [integrationMessengerModalIsOpen, setIntegrationMessengerModalIsOpen] =
    useState<boolean>(false);
  const [attachmentEditorModalIsOpen, setAttachmentEditorModalIsOpen] =
    useState<boolean>(false);
  const [audioPermissionsModalIsOpen, setAudioPermissionsModalIsOpen] =
    useState<boolean>(false);
  const [triggerDirectoryReload, setTriggerDirectoryReload] = useState(false);
  const [showDeleteFunnelModal, setShowDeleteFunnelModal] =
    useState<eDeleteFunnelProgressModal>(eDeleteFunnelProgressModal.HIDDEN);

  // RET-1739: a date filter counts as active as soon as EITHER end is set.
  // Counting only the end date meant a start-only range — the state that
  // actually filters the results — showed no badge and left the button
  // reading "Filtros" instead of "Filtros activos", so neither the customer
  // nor support could see why a view had gone empty.
  const activeFilterCount = [
    !!messageReadFilter,
    isDateFilterActive(dateRange),
    !!integrationFilter,
    !!statusFilter,
    !!(tagsFilter && tagsFilter.length > 0),
    !!assignedUserFilters,
    undeliveredFilter,
  ].filter(Boolean).length;
  const arePipeFiltersActive = activeFilterCount > 0;
  const directoryFilterCount = [
    directoryFunnelFilter !== 'all',
    directoryStageFilter !== 'all',
  ].filter(Boolean).length;

  const resetFilters = useCallback(() => {
    setMessageReadFilter(undefined);
    setIntegrationFilter(undefined);
    setStatusFilter(undefined);
    setTagsFilter(undefined);
    setAssignedUserFilters(undefined);
    setDateRange([undefined, undefined]);
    setDateFilterType(undefined);
    // Outside the Directory both are already 'all', so this changes nothing.
    setDirectoryFunnelFilter('all');
    setDirectoryStageFilter('all');
    setStoredTagsMatch(undefined);
    setUndeliveredFilter(false);
  }, [setMessageReadFilter, setIntegrationFilter, setStatusFilter, setTagsFilter, setAssignedUserFilters, setDateRange, setDateFilterType, setStoredTagsMatch]);

  return useMemo(
    () => ({
      addWorkspaceModelIsOpen,
      setAddWorkspaceModalIsOpen,
      connectionModalIsOpen: connectionModalIsOpen,
      setConnectionModalIsOpen: setConnectionModalIsOpen,
      disconnectionModalIsOpen: disconnectionModalIsOpen,
      setDisconnectionModalIsOpen: setDisconnectionModalIsOpen,
      metaConnectionModalIsOpen: metaConnectionModalIsOpen,
      setMetaConnectionModalIsOpen: setMetaConnectionModalIsOpen,
      metaConnectionModalReconnectRequested:
        metaConnectionModalReconnectRequested,
      setMetaConnectionModalReconnectRequested:
        setMetaConnectionModalReconnectRequested,
      metaPagesModalIsOpen: metaPagesModalIsOpen,
      setMetaPagesModalIsOpen: setMetaPagesModalIsOpen,
      whatsAppBusinessConnectionModalIsOpen: whatsAppBusinessConnectionModalIsOpen,
      whatsAppBusinessConnectionModalSource,
      whatsAppBusinessConnectionModalExitDestination,
      setWhatsAppBusinessConnectionModalIsOpen: setWhatsAppBusinessConnectionModalIsOpen,
      syncConversationsStatus,
      setSyncConversationsStatus,
      showNoPaidModal,
      toggleNoPaidModal,
      showPastDueModal,
      togglePastDueModal,
      showTrialConfirmModal,
      toggleTrialConfirmModal,
      showPrevPastDueModal,
      togglePrevPastDueModal,
      toggleBankDetailsModal,
      showBankDetailsModal,
      changeServiceModalIsOpen,
      setChangeServiceModalIsOpen: setChangeServiceModalIsOpen,
      addOptionsModalIsOpen,
      setAddOptionsModalIsOpen: setAddOptionsModalIsOpen,
      deleteOptionModalIsOpen,
      setDeleteOptionModalIsOpen: setDeleteOptionModalIsOpen,
      changeFunnelModalIsOpen,
      setChangeFunnelModalIsOpen: setChangeFunnelModalIsOpen,
      showResendModal,
      setShowResendModal,
      advanceSearchModalIsOpen,
      setAdvanceSearchModalIsOpen,
      selectedLeadForChatModal,
      setSelectedLeadForChatModal,
      focusedChatEvent,
      setFocusedChatEvent,
      forceDisconnectionModalIsOpen,
      setForceDisconnectionModalIsOpen,
      openAdvanceFiltersModal,
      setOpenAdvanceFiltersModal,
      messageReadFilter,
      setMessageReadFilter,
      dateRange,
      setDateRange,
      dateFilterType,
      setDateFilterType,
      undeliveredFilter,
      setUndeliveredFilter,
      integrationFilter,
      setIntegrationFilter,
      statusFilter,
      setStatusFilter,
      tagsFilter,
      setTagsFilter,
      assignedUserFilters,
      setAssignedUserFilters,
      directoryFunnelFilter,
      setDirectoryFunnelFilter,
      directoryStageFilter,
      setDirectoryStageFilter,
      directoryTagsMatch,
      setDirectoryTagsMatch,
      directoryFunnels,
      setDirectoryFunnels,
      directoryFilterCount,
      activeFilterCount,
      arePipeFiltersActive,
      resetFilters,
      integrationWhatsAppModalIsOpen,
      setIntegrationWhatsAppModalIsOpen,
      integrationInstagramModalIsOpen,
      setIntegrationInstagramModalIsOpen,
      integrationMessengerModalIsOpen,
      setIntegrationMessengerModalIsOpen,
      attachmentEditorModalIsOpen,
      setAttachmentEditorModalIsOpen,
      audioPermissionsModalIsOpen,
      setAudioPermissionsModalIsOpen,
      chatModalIsOpen,
      setChatModalIsOpen,
      maximizeModal,
      setMaximizeModal,
      currentFunnelStatus,
      setCurrentFunnelStatus,
      triggerDirectoryReload,
      setTriggerDirectoryReload,
      showSubscribeIntegrationsModal,
      setShowSubscribeIntegrationsModal,
      showSubscribeAddUsersModal,
      setShowSubscribeAddUsersModal,
      showSubscribeImportLeadsModal,
      setShowSubscribeImportLeadsModal,
      addOptionsSecondLevelModalIsOpen,
      setAddOptionsSecondLevelModalIsOpen,
      addOptionsThirdLevelModalIsOpen,
      setAddOptionsThirdLevelModalIsOpen,
      changesDataModalIsOpen,
      setChangesDataModalIsOpen,
      showDeleteFunnelModal,
      setShowDeleteFunnelModal,
      showArchiveLeadsModal,
      setShowArchiveLeadsModal,
      tiktokConnectionModalIsOpen,
      setTiktokConnectionModalIsOpen,
      tagOpsInFlight,
    }),
    [
      addWorkspaceModelIsOpen,
      setAddWorkspaceModalIsOpen,
      connectionModalIsOpen,
      setConnectionModalIsOpen,
      disconnectionModalIsOpen,
      setDisconnectionModalIsOpen,
      metaConnectionModalIsOpen,
      setMetaConnectionModalIsOpen,
      metaConnectionModalReconnectRequested,
      setMetaConnectionModalReconnectRequested,
      metaPagesModalIsOpen,
      setMetaPagesModalIsOpen,
      whatsAppBusinessConnectionModalIsOpen,
      whatsAppBusinessConnectionModalSource,
      whatsAppBusinessConnectionModalExitDestination,
      setWhatsAppBusinessConnectionModalIsOpen,
      syncConversationsStatus,
      setSyncConversationsStatus,
      showNoPaidModal,
      toggleNoPaidModal,
      showPastDueModal,
      togglePastDueModal,
      showTrialConfirmModal,
      toggleTrialConfirmModal,
      showPrevPastDueModal,
      togglePrevPastDueModal,
      toggleBankDetailsModal,
      showBankDetailsModal,
      changeServiceModalIsOpen,
      setChangeServiceModalIsOpen,
      addOptionsModalIsOpen,
      setAddOptionsModalIsOpen,
      deleteOptionModalIsOpen,
      setDeleteOptionModalIsOpen,
      changeFunnelModalIsOpen,
      setChangeFunnelModalIsOpen,
      showResendModal,
      setShowResendModal,
      advanceSearchModalIsOpen,
      setAdvanceSearchModalIsOpen,
      selectedLeadForChatModal,
      setSelectedLeadForChatModal,
      focusedChatEvent,
      setFocusedChatEvent,
      forceDisconnectionModalIsOpen,
      setForceDisconnectionModalIsOpen,
      openAdvanceFiltersModal,
      setOpenAdvanceFiltersModal,
      messageReadFilter,
      setMessageReadFilter,
      dateRange,
      setDateRange,
      dateFilterType,
      setDateFilterType,
      undeliveredFilter,
      setUndeliveredFilter,
      integrationFilter,
      setIntegrationFilter,
      statusFilter,
      setStatusFilter,
      tagsFilter,
      setTagsFilter,
      assignedUserFilters,
      setAssignedUserFilters,
      directoryFunnelFilter,
      setDirectoryFunnelFilter,
      directoryStageFilter,
      setDirectoryStageFilter,
      directoryTagsMatch,
      setDirectoryTagsMatch,
      directoryFunnels,
      setDirectoryFunnels,
      directoryFilterCount,
      activeFilterCount,
      arePipeFiltersActive,
      resetFilters,
      integrationWhatsAppModalIsOpen,
      setIntegrationWhatsAppModalIsOpen,
      integrationInstagramModalIsOpen,
      setIntegrationInstagramModalIsOpen,
      integrationMessengerModalIsOpen,
      setIntegrationMessengerModalIsOpen,
      attachmentEditorModalIsOpen,
      setAttachmentEditorModalIsOpen,
      audioPermissionsModalIsOpen,
      setAudioPermissionsModalIsOpen,
      chatModalIsOpen,
      setChatModalIsOpen,
      maximizeModal,
      setMaximizeModal,
      currentFunnelStatus,
      setCurrentFunnelStatus,
      triggerDirectoryReload,
      setTriggerDirectoryReload,
      showSubscribeIntegrationsModal,
      setShowSubscribeIntegrationsModal,
      showSubscribeAddUsersModal,
      setShowSubscribeAddUsersModal,
      showSubscribeImportLeadsModal,
      setShowSubscribeImportLeadsModal,
      addOptionsSecondLevelModalIsOpen,
      setAddOptionsSecondLevelModalIsOpen,
      addOptionsThirdLevelModalIsOpen,
      setAddOptionsThirdLevelModalIsOpen,
      changesDataModalIsOpen,
      setChangesDataModalIsOpen,
      showDeleteFunnelModal,
      setShowDeleteFunnelModal,
      showArchiveLeadsModal,
      setShowArchiveLeadsModal,
      tiktokConnectionModalIsOpen,
      setTiktokConnectionModalIsOpen,
      tagOpsInFlight,
    ],
  );
};

export default useModals;
